/**
 * DashboardConciergeDrawer — MS572 shell + MS574 Browse tab + MS582
 * responsive push-mode at ≥1920px.
 *
 * Right-side drawer that hosts the concierge surface INSIDE the
 * dashboard so the buyer can adjust a recipient's gift without losing
 * dashboard context. Pre-MS572, the ApprovalModal's "Adjust" button
 * navigated to /chat — the buyer left the dashboard entirely and lost
 * their place. This drawer preserves context by rendering the
 * concierge alongside (push-mode, ≥1920px) or over (overlay-Sheet,
 * <1920px) the celebration list.
 *
 * Layout modes (MS582):
 *
 *   `mode="overlay"` (default below 1920px) — shadcn `<Sheet>` portal,
 *   slides in from the right, ~500px wide, dims the backdrop. Intimate
 *   single-task focus. The pre-MS582 behaviour.
 *
 *   `mode="push"` (default at ≥1920px) — inline `<aside>` rendered
 *   alongside the list pane as a permanent 3rd column. No portal, no
 *   backdrop, list pane stays visible. Workbench pattern (Linear's
 *   issue detail rail, Stripe's customer detail). Buyers managing 12+
 *   concurrent celebrations can see the list while editing a single
 *   recipient.
 *
 * Mode resolution: caller passes an explicit `mode` prop. Dashboard.tsx
 * derives this from `useIsLargeScreen()` so the same component handles
 * both rendering paths without the consumer (the drawer itself) having
 * to know the viewport. This keeps the responsive logic in ONE place
 * (the Dashboard, where the grid template also changes) and lets the
 * drawer be unit-tested in either mode by passing `mode` directly.
 *
 * State invariant (critical for MS582): all persistent state lives at
 * the drawer's top level — recipient context, active tab, draft scope,
 * Browse SKU — and is shared between both modes. Switching modes (e.g.
 * user drags the window from 1366→2560) only changes WHERE the content
 * mounts, not WHAT is mounted. The inner content is a single rendered
 * subtree wrapped in either the Sheet or an inline aside.
 *
 * Tab structure (MS574):
 *   - Concierge (default active) — InlineConcierge, the same chat
 *     surface as the landing hero + FloatingConcierge widget.
 *   - Browse — recipient-aware catalog grid; View opens
 *     ProductDetailModal via portaled checkout flow (MS61a parity).
 *
 * Why a tab switcher (not separate drawers / inline browse below
 * chat / two-pane layout):
 *   - The drawer is 500px wide on desktop; a two-pane layout would
 *     starve both panes. Tabs let each surface use the full width.
 *   - "Inline browse below chat" was considered but the chat surface
 *     is variable-height (scrolls with conversation), which would
 *     push the browse grid off-screen. Tabs give browse its own
 *     scroll context.
 *   - "Two drawers" (separate Browse drawer) was rejected because the
 *     buyer's mental model is "I'm adjusting Sarah's gift" — they
 *     want to flip between asking and picking without re-launching.
 *
 * State persistence across tab switches:
 *   - Both tabs stay MOUNTED via `forceMount` so InlineConcierge
 *     keeps its conversation state when the buyer switches to Browse
 *     and back. Same for the Browse tab's selected SKU. MS573's draft
 *     persistence (chat_sessions.is_active_draft) lands separately;
 *     this drawer just keeps the React state alive across tab toggles
 *     so MS573's wiring doesn't fight a remount on every flip.
 *   - Visibility is controlled by `data-state` (Radix Tabs) + Tailwind
 *     `data-[state=inactive]:hidden`, NOT by conditional render. The
 *     hidden tab's React tree stays in place so all hooks keep
 *     running (chat polling, browse fetch results, etc.).
 *
 * Close behavior (MS574):
 *   - Closing the drawer with a Browse SKU selected (but not yet
 *     ordered) discards the selection silently — no confirm prompt.
 *     The buyer can pick a different SKU on reopen. A confirm-on-
 *     close forward-friction isn't justified at this stage.
 *   - In push-mode, "close" means the column disappears (parent flips
 *     `recipient` → null) — the close button still lives in the inner
 *     header so the buyer has a deterministic dismiss affordance even
 *     when there's no Sheet backdrop to click. Escape still works
 *     because the Sheet is unused in push-mode; the inline mode adds
 *     a keydown listener directly.
 *
 * Sibling surfaces (unchanged):
 *   - FloatingConcierge stays the chat affordance on every authed tab
 *     EXCEPT /dashboard (MS575 hides it there).
 *   - Standalone /chat and /browse routes are untouched.
 */

import { useEffect } from "react";
import { Maximize2, Minimize2, X } from "lucide-react";
import { ConciergeFulfillmentSurface } from "@/components/concierge/ConciergeFulfillmentSurface";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useAuth } from "@/hooks/useAuth";
import type { FulfillmentReturnContext } from "@/lib/fulfillmentReturn";
import type { DashboardClientActionHandlers } from "@/lib/dashboardClientActions";
import { FulfillmentReturnLink } from "@/components/lembra/FulfillmentReturnLink";
import { humaniseOccasionShort } from "@/lib/humaniseOccasion";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";

/** MS572: shape of the recipient context the drawer opens against.
 *  Mirrors the celebration-row fields Dashboard already has on hand
 *  (id + name + occasion + celebration id) so the eventual MS575
 *  handoff from ApprovalModal can pass the same data shape without
 *  re-deriving anything. `null` → drawer closed; non-null → drawer
 *  opens with that recipient pre-loaded into the chat brief. */
export interface DashboardConciergeDrawerRecipient {
  /** team_members.id — for the chat brief's recipient_id slot. */
  id: string;
  /** Display name shown in the drawer header (e.g. "Sarah Chen"). */
  name: string;
  /** Occasion like "birthday" / "anniversary" / "first day". Optional
   *  because some adjust flows (e.g. ad-hoc gifting) won't have one;
   *  the header copy degrades gracefully when missing. */
  occasion?: string;
  /** celebration_id, threaded through to the chat brief so a gift
   *  ordered via the drawer links back to the originating celebration
   *  row (parity with the existing /chat handoff via
   *  celebrationToChatUrl). */
  celebrationId?: string;
  /** celebrations.date (ISO) — pre-fills OrderConfirmSheet delivery date. */
  celebrationDate?: string;
  /** MS574: optional dietary_requirements pulled from team_members so
   *  the Browse tab can auto-filter the catalog for the recipient's
   *  preferences. Buyer can clear the filter from inside FilterRail
   *  to see the full catalog. Undefined when the celebration row
   *  wasn't joined to team_members.dietary_requirements (e.g. the
   *  current dev-trigger pathway); the Browse tab just opens with
   *  no dietary pre-fill in that case. */
  dietaryRequirements?: string[];
}

/** MS582: layout mode picked by the parent based on viewport.
 *  - `"overlay"` (default below 1920px) — Sheet portal with backdrop.
 *  - `"push"` (default ≥1920px) — inline aside column; no portal.
 *  Caller is responsible for deriving this (e.g. via useIsLargeScreen)
 *  so Dashboard.tsx can ALSO restructure its grid template in lockstep
 *  with the drawer's mode — keeping both decisions in one place. */
export type DashboardConciergeDrawerMode = "overlay" | "push";

export interface DashboardConciergeDrawerProps {
  /** When null, the drawer is closed (Sheet `open={false}` in overlay
   *  mode; no aside rendered in push mode). When non-null, the drawer
   *  opens with that recipient pre-loaded as the initial brief. The
   *  parent (Dashboard) owns this state. */
  recipient: DashboardConciergeDrawerRecipient | null;
  /** Fired when the user closes the drawer (close button, escape,
   *  backdrop click in overlay mode; close button or escape in push
   *  mode). Parent should set `recipient` back to null. */
  onClose: () => void;
  /** MS582: layout mode. Defaults to `"overlay"` so existing callers
   *  (and the regression test suite) get the pre-MS582 behaviour
   *  without opting in. Dashboard.tsx flips to `"push"` at ≥1920px. */
  mode?: DashboardConciergeDrawerMode;
  /** Explicit return when fulfillment started on another route (e.g. Orders). */
  fulfillmentReturn?: FulfillmentReturnContext | null;
  onDashboardClientActions?: DashboardClientActionHandlers | null;
}

/**
 * Convert a recipient context into the InlineConcierge initialBrief.
 *
 * Exported so the regression test can pin the shape without rendering
 * the full drawer. `raw_text` is intentionally empty: this drawer
 * opens with an empty composer so the user types their adjustment
 * themselves (mirrors FloatingConcierge's `{ raw_text: "" }` pattern).
 * If we pre-filled `raw_text`, useInlineConversation's
 * autoSendFirstMessage would auto-fire it — premature for an
 * "adjust" flow where the user hasn't said anything yet.
 */
export function recipientToInitialBrief(
  recipient: DashboardConciergeDrawerRecipient,
): {
  raw_text: string;
  recipient_id: string;
  recipient_name: string;
  occasion?: string;
  celebration_id?: string;
  delivery_date?: string;
} {
  const brief: {
    raw_text: string;
    recipient_id: string;
    recipient_name: string;
    occasion?: string;
    celebration_id?: string;
    delivery_date?: string;
  } = {
    raw_text: "",
    recipient_id: recipient.id,
    recipient_name: recipient.name,
  };
  if (recipient.occasion) brief.occasion = recipient.occasion;
  if (recipient.celebrationId) brief.celebration_id = recipient.celebrationId;
  if (recipient.celebrationDate) brief.delivery_date = recipient.celebrationDate;
  return brief;
}

type DrawerWidth = "standard" | "expanded";

const DRAWER_WIDTH_PX: Record<DrawerWidth, number> = {
  standard: 500,
  expanded: 720,
};

/** Width class for overlay SheetContent. Exported for tests. */
export function overlayWidthClass(width: DrawerWidth): string {
  return width === "expanded"
    ? "w-full sm:max-w-[720px]"
    : "w-full sm:max-w-[500px]";
}

export function DashboardConciergeDrawer({
  recipient,
  onClose,
  mode = "overlay",
  fulfillmentReturn = null,
  onDashboardClientActions = null,
}: DashboardConciergeDrawerProps) {
  const open = recipient !== null;
  // MS573: the drawer's draft persistence lifecycle is scoped to
  // (user_id, recipient_id) — both flow through to useInlineConversation
  // via the InlineConcierge props below. Authed-only by design (the
  // dashboard is gated behind auth; the drawer never mounts in the
  // anon flow), so user.id is non-null whenever recipient is non-null.
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [drawerWidth, setDrawerWidth] = usePersistedState<DrawerWidth>(
    "lembra:dashboard-drawer-width",
    "standard",
    {
      validate: (v): v is DrawerWidth =>
        v === "standard" || v === "expanded",
    },
  );

  // MS582 push-mode Escape handler. In overlay mode the Sheet
  // primitive already wires Escape to onOpenChange(false) (which we
  // forward to onClose); in push mode there's no Sheet, so we add a
  // window keydown listener ourselves. Scoped to the push branch so
  // we don't double-fire onClose in overlay mode (Sheet's handler +
  // ours would both run). Only active when the drawer is open in
  // push mode — otherwise a stray Escape on the dashboard's empty
  // detail pane would surprise-fire onClose with no drawer to close.
  useEffect(() => {
    if (mode !== "push" || !open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, open, onClose]);

  const toggleDrawerWidth = () => {
    setDrawerWidth((w) => (w === "standard" ? "expanded" : "standard"));
  };

  const columnWidthPx = DRAWER_WIDTH_PX[drawerWidth];

  // Format the header occasion for readability. "work_anniversary" →
  // "anniversary", "welcome" → "first day" — mirrors the humanType
  // mapping inside Dashboard.tsx so the drawer header reads the same
  // language the list/detail panes use.
  const occasionLabel =
    recipient?.occasion ? humaniseOccasionShort(recipient.occasion) : null;

  // MS582: the inner content — recipient header + tabs + panels — is
  // identical between overlay and push modes. Extract into a single
  // JSX expression and reuse so the React tree (and thus all hooks
  // inside InlineConcierge / DrawerBrowsePanel) stays identical
  // across modes. Different parent wrappers (Sheet vs aside) only
  // change WHERE this subtree mounts, not WHAT mounts.
  const innerContent = recipient ? (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ── Recipient context header ──────────────────────────────
          In push-mode this header also carries the close affordance
          (the small × button) because there's no Sheet chrome to
          provide one. In overlay-mode the Sheet renders its own ×
          in the top-right corner; we still render a header for the
          recipient context but skip our local close button so the
          buyer doesn't see two ×'s. */}
      <header
        className="border-b bg-white px-6 py-4 shrink-0 flex items-start justify-between gap-3"
        style={{ borderColor: "rgba(29,52,97,0.08)" }}
        data-testid="drawer-recipient-header"
      >
        <div className="min-w-0">
          {fulfillmentReturn && (
            <FulfillmentReturnLink context={fulfillmentReturn} className="mb-2" />
          )}
          <p
            className="text-[10px] font-semibold uppercase tracking-[0.18em] text-coral/85"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            }}
          >
            Adjusting for
          </p>
          <p
            className="mt-1 text-[18px] text-navy"
            style={{
              fontFamily: "Newsreader, Georgia, serif",
              fontStyle: "italic",
              fontWeight: 500,
              lineHeight: 1.2,
              color: "rgba(29,52,97,1)",
            }}
          >
            {recipient.name}
            {occasionLabel ? <>'s {occasionLabel}</> : null}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <button
              type="button"
              onClick={toggleDrawerWidth}
              aria-label={
                drawerWidth === "standard" ? "Expand drawer" : "Shrink drawer"
              }
              data-testid="drawer-width-toggle"
              className="rounded-sm p-1.5 text-charcoal/55 hover:text-navy hover:bg-[rgba(29,52,97,0.06)] transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
            >
              {drawerWidth === "standard" ? (
                <Maximize2 className="h-4 w-4" />
              ) : (
                <Minimize2 className="h-4 w-4" />
              )}
            </button>
          {mode === "push" && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="drawer-push-close"
              className="rounded-sm p-1.5 text-charcoal/55 hover:text-navy hover:bg-[rgba(29,52,97,0.06)] transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      {userId && (
        <div className="flex min-h-0 flex-1 flex-col px-2 pb-2">
          <ConciergeFulfillmentSurface
            recipient={recipient}
            userId={userId}
            onDashboardClientActions={onDashboardClientActions}
            fulfillmentReturn={fulfillmentReturn}
            shell="drawer"
          />
        </div>
      )}
    </div>
    ) : null;

  // ── Push-mode render — inline column ────────────────────────────
  // Returns nothing when closed (recipient === null) so the parent
  // grid collapses the column. data-testid mirrors the overlay path
  // so a single regression assertion can target "drawer is open"
  // regardless of mode. The aside intentionally has no transition —
  // the open/close is decided by parent state, and a slide-in would
  // fight the Dashboard grid template change. The mode-flip itself
  // is rare (window resize across 1920px); the user-visible mount
  // happens via the Adjust button which fires INSIDE a stable mode.
  if (mode === "push") {
    if (!open) return null;
    return (
      <>
        <aside
          data-testid="dashboard-concierge-drawer-push"
          data-drawer-mode="push"
          data-drawer-width={drawerWidth}
          className="flex h-full min-h-0 flex-col border-l bg-cream shrink-0"
          style={{
            borderColor: "rgba(29,52,97,0.08)",
            width: columnWidthPx,
          }}
          aria-label={
            recipient
              ? `Adjusting concierge for ${recipient.name}${
                  occasionLabel ? `'s ${occasionLabel}` : ""
                }`
              : "Concierge drawer"
          }
        >
          {innerContent}
        </aside>
      </>
    );
  }

  // ── Overlay-mode render — Sheet portal (default) ────────────────
  return (
    <>
      <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        data-testid="dashboard-concierge-drawer-overlay"
        data-drawer-mode="overlay"
        data-drawer-width={drawerWidth}
        className={`${overlayWidthClass(drawerWidth)} flex flex-col p-0 gap-0 bg-cream transition-[max-width] duration-200`}
      >
        {/* Accessible title for the Radix dialog. Visually hidden via
            sr-only because we render our own richer recipient header
            below. */}
        <SheetTitle className="sr-only">
          {recipient
            ? `Adjusting concierge for ${recipient.name}${
                occasionLabel ? `'s ${occasionLabel}` : ""
              }`
            : "Concierge drawer"}
        </SheetTitle>

        {innerContent}
      </SheetContent>
    </Sheet>
    </>
  );
}
